import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { content, firmInfo } from '../mock';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';

const Contact = () => {
  const { language } = useLanguage();
  const t = content[language];

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${firmInfo.whatsapp.replace(/[^0-9]/g, '')}`, '_blank');
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-amber-50 rounded-full mb-6">
            <span className="text-amber-600 font-semibold text-sm">{t.contact.title}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            {t.contact.title}
          </h2>
          <p className="text-lg text-slate-600">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-start">
          {/* Contact Info Cards */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 hover:shadow-xl transition-all">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{t.contact.phone}</h3>
                  <a href={`tel:${firmInfo.phone}`} className="text-slate-600 hover:text-amber-600 transition-colors text-lg">
                    {firmInfo.phone}
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 hover:shadow-xl transition-all">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-slate-600 to-slate-800 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{t.contact.email}</h3>
                  <a href={`mailto:${firmInfo.email}`} className="text-slate-600 hover:text-amber-600 transition-colors break-all">
                    {firmInfo.email}
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 hover:shadow-xl transition-all">
              <a 
                href="https://maps.app.goo.gl/aKR2Tf54NqjJ3ASe7?g_st=ic"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 group"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <MapPin className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-amber-600 transition-colors">{t.contact.location}</h3>
                  <p className="text-slate-600 group-hover:text-amber-600 transition-colors">{t.contact.locationDetail}</p>
                  <p className="text-xs text-amber-600 mt-2 font-semibold">
                    {language === 'ar' ? '← انقر للحصول على الاتجاهات' : 'Click for directions →'}
                  </p>
                </div>
              </a>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 hover:shadow-xl transition-all">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-slate-600 to-slate-800 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{t.contact.hours}</h3>
                  <p className="text-slate-600 whitespace-pre-line">{t.contact.hoursDetail}</p>
                </div>
              </div>
            </div>

            {/* WhatsApp Button */}
            <Button
              onClick={handleWhatsApp}
              className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-6 text-lg shadow-xl hover:shadow-2xl transition-all group"
            >
              <MessageCircle className={`w-6 h-6 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
              {language === 'ar' ? 'تواصل عبر واتساب' : 'Contact via WhatsApp'}
            </Button>
          </div>

          {/* Map */}
          <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-200">
            <div className="w-full h-[500px] bg-slate-100 rounded-xl overflow-hidden relative group">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13905.315379717478!2d48.078260799999995!3d29.368384399999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3fcf9d2e96c04f9d%3A0x5a8e8e8e8e8e8e8e!2z2KfZhNmD2YjZitiq!5e0!3m2!1sar!2skw!4v1234567890123!5m2!1sar!2skw"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="AL-DURAR LAW FIRM Location"
              ></iframe>
              
              {/* Overlay with "Open in Google Maps" button */}
              <a
                href="https://maps.app.goo.gl/aKR2Tf54NqjJ3ASe7?g_st=ic"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute inset-0 flex items-center justify-center bg-slate-900/0 hover:bg-slate-900/20 transition-all opacity-0 group-hover:opacity-100"
              >
                <div className="bg-white rounded-lg px-6 py-3 shadow-xl transform scale-95 group-hover:scale-100 transition-transform">
                  <span className="text-slate-900 font-semibold flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-amber-600" />
                    {language === 'ar' ? 'فتح في خرائط جوجل' : 'Open in Google Maps'}
                  </span>
                </div>
              </a>
            </div>
            
            {/* Get Directions Button */}
            <a
              href="https://maps.app.goo.gl/aKR2Tf54NqjJ3ASe7?g_st=ic"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 block"
            >
              <Button className="w-full bg-gradient-to-r from-slate-600 to-slate-800 hover:from-slate-700 hover:to-slate-900 text-white py-4 shadow-lg">
                <MapPin className={`w-5 h-5 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                {language === 'ar' ? 'احصل على الاتجاهات' : 'Get Directions'}
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;